import React, { useState } from 'react';
import { Wallet, ArrowUpRight, ArrowDownLeft, IndianRupee, Smartphone, CreditCard, Building2, Banknote, Plus, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { walletBalance, transactions } from '../mock/mockData';
import { toast } from '../hooks/use-toast';

const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

const methodIcon = (m) => {
  if (m === 'UPI') return Smartphone;
  if (m === 'Card') return CreditCard;
  if (m === 'NetBanking' || m === 'RTGS' || m === 'NEFT') return Building2;
  if (m === 'COD') return Banknote;
  return Wallet;
};

export default function Payments() {
  const [payOpen, setPayOpen] = useState(false);
  const [method, setMethod] = useState('upi');
  const [amount, setAmount] = useState('');
  const [upiId, setUpiId] = useState('');

  const submitPay = () => {
    if (!amount) { toast({ title: 'Enter amount', description: 'Please enter payment amount.' }); return; }
    setPayOpen(false);
    const methodMap = { upi: 'UPI', card: 'Card', netbanking: 'NetBanking', rtgs: 'RTGS', cod: 'COD' };
    toast({ title: 'Payment initiated!', description: `${formatINR(parseInt(amount))} via ${methodMap[method]}. Reference: TXN${Math.floor(Math.random()*100000)}` });
    setAmount(''); setUpiId('');
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-[#0B2545] font-[Poppins]">Payments & Wallet</h1>
          <p className="text-sm text-gray-500 mt-1">UPI, RTGS, NEFT, Card & COD — all in one place.</p>
        </div>
        <Dialog open={payOpen} onOpenChange={setPayOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#00A651] hover:bg-[#008a43] text-white"><Plus className="w-4 h-4 mr-2" />New Payment</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Make a Payment</DialogTitle></DialogHeader>
            <div className="py-2 space-y-4">
              <div>
                <Label className="text-sm">Amount (₹)</Label>
                <Input type="number" placeholder="10000" className="mt-1.5 h-11 text-lg" value={amount} onChange={e => setAmount(e.target.value)} />
              </div>
              <div>
                <Label className="text-sm">Payment Method</Label>
                <RadioGroup value={method} onValueChange={setMethod} className="mt-2 grid grid-cols-2 gap-2">
                  {[
                    { v: 'upi', label: 'UPI', desc: 'GPay, PhonePe, Paytm', Icon: Smartphone },
                    { v: 'card', label: 'Card', desc: 'Debit / Credit', Icon: CreditCard },
                    { v: 'netbanking', label: 'Net Banking', desc: 'All major banks', Icon: Building2 },
                    { v: 'rtgs', label: 'RTGS / NEFT', desc: 'For large amounts', Icon: Building2 },
                    { v: 'cod', label: 'COD', desc: 'Cash on Delivery', Icon: Banknote },
                  ].map(o => (
                    <label key={o.v} htmlFor={o.v} className={`border rounded-xl p-3 cursor-pointer transition-all ${method === o.v ? 'border-[#00A651] bg-[#00A651]/5' : 'border-gray-200 hover:border-gray-300'}`}>
                      <div className="flex items-start gap-2">
                        <RadioGroupItem value={o.v} id={o.v} className="mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center gap-1.5"><o.Icon className="w-4 h-4 text-[#0B2545]" /><span className="font-semibold text-sm text-[#0B2545]">{o.label}</span></div>
                          <p className="text-xs text-gray-500 mt-0.5">{o.desc}</p>
                        </div>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>
              {method === 'upi' && (
                <div>
                  <Label className="text-sm">UPI ID / Phone</Label>
                  <Input placeholder="driver@paytm or 98765 43210" className="mt-1.5" value={upiId} onChange={e => setUpiId(e.target.value)} />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
              <Button onClick={submitPay} className="bg-[#00A651] hover:bg-[#008a43] text-white">Pay {amount && formatINR(parseInt(amount))}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Wallet cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="md:col-span-2 bg-gradient-to-br from-[#0B2545] to-[#13315C] rounded-2xl p-6 text-white relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#00A651]/20 rounded-full blur-2xl"></div>
          <div className="flex items-center gap-2 mb-2"><Wallet className="w-5 h-5" /><span className="text-sm text-white/70">Wallet Balance</span></div>
          <p className="text-4xl font-extrabold font-[Poppins]">{formatINR(walletBalance.available)}</p>
          <p className="text-sm text-white/70 mt-1">Available to withdraw</p>
          <div className="flex gap-2 mt-6 relative">
            <Button size="sm" onClick={() => toast({ title: 'Withdraw', description: 'Bank transfer initiated to Kotak XXXX4523' })} className="bg-white text-[#0B2545] hover:bg-white/90">Withdraw</Button>
            <Button size="sm" variant="outline" onClick={() => toast({ title: 'Add money', description: 'UPI added ₹5,000 to wallet.' })} className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white">Add Money</Button>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-2"><ArrowDownLeft className="w-4 h-4 text-[#00A651]" /><span className="text-xs text-gray-500">Received (Month)</span></div>
          <p className="text-xl font-extrabold text-[#0B2545] font-[Poppins]">{formatINR(walletBalance.monthReceived)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-2"><ArrowUpRight className="w-4 h-4 text-red-500" /><span className="text-xs text-gray-500">Paid (Month)</span></div>
          <p className="text-xl font-extrabold text-[#0B2545] font-[Poppins]">{formatINR(walletBalance.monthPaid)}</p>
        </div>
      </div>

      {/* Payment methods */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h2 className="text-lg font-bold text-[#0B2545] mb-4">Accepted Payment Methods</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { name: 'UPI', desc: 'Instant', Icon: Smartphone, color: 'bg-[#00A651]/10 text-[#00A651]' },
            { name: 'RTGS/NEFT', desc: 'Bank Transfer', Icon: Building2, color: 'bg-[#0B2545]/10 text-[#0B2545]' },
            { name: 'Debit/Credit', desc: 'Visa/Master/Rupay', Icon: CreditCard, color: 'bg-purple-50 text-purple-600' },
            { name: 'Net Banking', desc: 'All banks', Icon: Building2, color: 'bg-blue-50 text-blue-600' },
            { name: 'COD', desc: 'For POD freight', Icon: Banknote, color: 'bg-[#FFB800]/15 text-[#B37800]' },
          ].map(m => (
            <div key={m.name} className="border border-gray-100 rounded-xl p-3 text-center hover-lift">
              <div className={`w-10 h-10 rounded-lg ${m.color} flex items-center justify-center mx-auto mb-2`}>
                <m.Icon className="w-5 h-5" />
              </div>
              <p className="font-semibold text-sm text-[#0B2545]">{m.name}</p>
              <p className="text-[10px] text-gray-500">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Transactions */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h2 className="text-lg font-bold text-[#0B2545] mb-4">Recent Transactions</h2>
        <Tabs defaultValue="all">
          <TabsList className="bg-gray-50">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="received">Received</TabsTrigger>
            <TabsTrigger value="paid">Paid</TabsTrigger>
          </TabsList>
          {['all', 'received', 'paid'].map(t => (
            <TabsContent key={t} value={t} className="mt-4 space-y-2">
              {(t === 'all' ? transactions : transactions.filter(x => x.type === t)).map(tx => {
                const Icon = methodIcon(tx.method);
                return (
                  <div key={tx.id} className="flex items-center gap-4 py-3 border-b border-gray-50 last:border-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tx.type === 'received' ? 'bg-[#00A651]/10' : 'bg-red-50'}`}>
                      {tx.type === 'received' ? <ArrowDownLeft className="w-5 h-5 text-[#00A651]" /> : <ArrowUpRight className="w-5 h-5 text-red-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-[#0B2545] truncate">{tx.type === 'received' ? `From ${tx.from}` : `To ${tx.to}`}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Icon className="w-3 h-3" /><span>{tx.method}</span><span>•</span><span>{tx.date}</span><span>•</span><span className="font-mono">{tx.ref}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${tx.type === 'received' ? 'text-[#00A651]' : 'text-red-500'}`}>{tx.type === 'received' ? '+' : '−'}{formatINR(tx.amount)}</p>
                      <Badge className="bg-[#00A651]/10 text-[#00A651] border-0 text-[10px]">{tx.status}</Badge>
                    </div>
                  </div>
                );
              })}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
